import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { dateTimeFormatter } from '@nhl/formatter-utilities';
import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';

import commonMessages from '~/core/commonMessages';
import { PlayerPosition, PlayerStatAbbrev } from '~/core/enums';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { IceSurfacePlayer } from '../../../../types';

import {
    GreyDot,
    HeadshotContainer,
    PlayerName,
    PlayerPopoverContainer,
    PopoverContentContainer,
    PopoverInfo,
    TimeCategoryText,
    TimeLeft,
} from './styles';

type Props = {
    player: IceSurfacePlayer;
    teamAbbrev: ShortCodeEnums.TeamTriCode;
    isPenalty: boolean;
};

const PlayerPopover: React.FC<Props> = ({ player, teamAbbrev, isPenalty }) => {
    const theme = useTheme();
    const { headshot, name, positionCode, sweaterNumber, totalSOI, secondsRemaining } = player;

    return (
        <PlayerPopoverContainer>
            <svg xmlns="http://www.w3.org/2000/svg" width="152" height="178" viewBox="0 0 152 178" fill="none">
                <g filter="url(#popoverFilter)">
                    <path
                        d="M20.5333 4C15.8205 4 12 7.58172 12 12V134C12 138.418 15.8205 142 20.5333 142H65.5307L74.297 156.235C75.0781 157.503 76.9219 157.503 77.703 156.235L86.4693 142H131.467C136.18 142 140 138.418 140 134V12C140 7.58172 136.18 4 131.467 4H20.5333Z"
                        fill={theme.Surface.primary}
                    />
                </g>
                <defs>
                    <filter
                        id="popoverFilter"
                        x="0"
                        y="0"
                        width="152"
                        height="177.186"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset />
                        <feGaussianBlur stdDeviation="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1120_21160" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="4" />
                        <feGaussianBlur stdDeviation="4" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                        <feBlend
                            mode="normal"
                            in2="effect1_dropShadow_1120_21160"
                            result="effect2_dropShadow_1120_21160"
                        />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="8" />
                        <feGaussianBlur stdDeviation="6" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
                        <feBlend
                            mode="normal"
                            in2="effect2_dropShadow_1120_21160"
                            result="effect3_dropShadow_1120_21160"
                        />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_1120_21160" result="shape" />
                    </filter>
                </defs>
            </svg>
            <PopoverContentContainer>
                <HeadshotContainer>
                    <Headshot
                        type={positionCode === PlayerPosition.goalie ? headshotTypes.GOALIE : headshotTypes.SKATER}
                        urlOverride={headshot}
                        size={56}
                        backgroundColor={teamAbbrev ? theme.Team[teamAbbrev]?.gamecenter : theme.Surface.secondary}
                    />
                </HeadshotContainer>
                <PlayerName>{parseValueFromLocalizedAttribute(name)}</PlayerName>
                <PopoverInfo>
                    {teamAbbrev} <GreyDot>&bull;</GreyDot> #{sweaterNumber} <GreyDot>&bull;</GreyDot> {positionCode}
                </PopoverInfo>
                <div>
                    <TimeCategoryText>
                        <FormattedMessage
                            {...(isPenalty
                                ? commonMessages.tableHeaders.long.penalty
                                : commonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.timeOnIce])}
                        />
                        :
                    </TimeCategoryText>
                    &nbsp;
                    <TimeLeft>
                        {dateTimeFormatter.displaySecondsFormatted((isPenalty ? secondsRemaining : totalSOI) || 0)}
                    </TimeLeft>
                </div>
            </PopoverContentContainer>
        </PlayerPopoverContainer>
    );
};

export default PlayerPopover;
